import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';

export interface DataImport {
  id: string;
  user_id: string;
  file_name: string;
  file_size?: number;
  file_type: string;
  import_status: 'pending' | 'processing' | 'completed' | 'failed';
  records_processed: number;
  error_message?: string;
  metadata: any;
  created_at: string;
  completed_at?: string;
}

export const useDataImport = () => {
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);

  const importFile = async (file: File): Promise<DataImport | null> => {
    setImporting(true);
    setProgress(0);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to import data");
        return null;
      }

      // Create import record
      const { data: importRecord, error: importError } = await supabase
        .from('data_imports')
        .insert({
          user_id: user.id,
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          import_status: 'pending'
        })
        .select()
        .single();

      if (importError) throw importError;

      // Simulate file processing
      setProgress(10);
      
      // Read file content
      const text = await file.text();
      setProgress(30);

      let parsedData: any[] = [];
      
      if (file.type === 'application/json' || file.name.endsWith('.json')) {
        parsedData = JSON.parse(text);
      } else if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        // Simple CSV parsing
        const lines = text.split('\n');
        const headers = lines[0].split(',').map(h => h.trim());
        parsedData = lines.slice(1).filter(line => line.trim()).map(line => {
          const values = line.split(',');
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = values[index]?.trim();
          });
          return obj;
        });
      }

      setProgress(60);

      // Store processed data based on type
      if (parsedData.length > 0) {
        // Try to detect data type and store appropriately
        const sampleData = parsedData[0];
        
        // Prepare data for insertion
        const dataToInsert = parsedData.slice(0, 100).map(item => ({ // Limit to 100 records
          user_id: user.id,
          date: new Date().toISOString(),
          ...item
        }));

        setProgress(80);

        // Try inserting into appropriate table based on data structure
        if (sampleData.revenue || sampleData.profit || sampleData.expenses) {
          await supabase.from('finance_data').insert(dataToInsert);
        } else if (sampleData.conversion_rate || sampleData.cart_abandonment_rate) {
          await supabase.from('ecommerce_data').insert(dataToInsert);
        } else if (sampleData.user_engagement_rate || sampleData.active_users) {
          await supabase.from('tech_data').insert(dataToInsert);
        }
      }

      setProgress(90);

      // Update import status
      const { error: updateError } = await supabase
        .from('data_imports')
        .update({
          import_status: 'completed',
          records_processed: parsedData.length,
          completed_at: new Date().toISOString(),
          metadata: { 
            sample_data: parsedData.slice(0, 5),
            total_records: parsedData.length
          }
        })
        .eq('id', importRecord.id);

      if (updateError) throw updateError;

      setProgress(100);
      toast.success(`Successfully imported ${parsedData.length} records from ${file.name}`);
      trackEvent('data_uploaded', { file_name: file.name, records: parsedData.length });
      
      return { ...importRecord, import_status: 'completed', records_processed: parsedData.length };
    } catch (error) {
      console.error('Error importing file:', error);
      toast.error(`Failed to import ${file.name}: ${error instanceof Error ? error.message : 'Unknown error'}`);
      return null;
    } finally {
      setImporting(false);
      setTimeout(() => setProgress(0), 2000);
    }
  };

  const importFromIntegration = async (integration: 'google_sheets' | 'dropbox' | 'microsoft' | 'crm') => {
    setImporting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Please log in to import data");
        return;
      }

      // Call appropriate integration function
      const { data, error } = await supabase.functions.invoke('sync-crm-data', {
        body: { integration, user_id: user.id }
      });

      if (error) throw error;

      // Create import record
      await supabase.from('data_imports').insert({
        user_id: user.id,
        file_name: `${integration}_sync_${Date.now()}`,
        file_type: 'integration',
        import_status: 'completed',
        records_processed: data?.recordsProcessed || 0,
        metadata: { integration, sync_time: new Date().toISOString() }
      });

      toast.success(`Successfully synced data from ${integration}`);
    } catch (error) {
      console.error('Error syncing integration:', error);
      toast.error(`Failed to sync ${integration} data`);
    } finally {
      setImporting(false);
    }
  };

  return {
    importing,
    progress,
    importFile,
    importFromIntegration
  };
};